/**
 * Who is talking, from the audio rather than from the mute button.
 *
 * An `AnalyserNode` per remote track and one on the local microphone, sampled
 * ten times a second. Two thresholds, not one: a single threshold makes the
 * ring around somebody's tile flicker on every pause between words, because
 * ordinary speech crosses any fixed line several times a second. So a level
 * above `SPEAKING_ON` starts it, and only a level under `SPEAKING_OFF` held for
 * `SPEAKING_HOLD_MS` ends it.
 *
 * The local microphone is watched **before** the mute button is applied, on
 * purpose: somebody speaking while muted should light up, so they can be told.
 * `CallSession` never stops the track, so the samples are there to read even
 * when `enabled` is false — the analyser sees the microphone, not the packets.
 */

/** Root mean square of a time-domain window, which is loudness as a number
 *  between 0 and 1. Exported because it is the one part of this file that can
 *  be checked without an audio stack. */
export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const s of samples) sum += s * s;
  return Math.sqrt(sum / samples.length);
}

export const SPEAKING_ON = 0.05;
export const SPEAKING_OFF = 0.025;
export const SPEAKING_HOLD_MS = 350;

/** The threshold pair, with the memory between them. */
export class SpeechGate {
  private speaking = false;
  private quietSince = 0;

  constructor(
    private readonly onLevel = SPEAKING_ON,
    private readonly offLevel = SPEAKING_OFF,
    private readonly holdMs = SPEAKING_HOLD_MS
  ) {}

  get active(): boolean {
    return this.speaking;
  }

  /** The state after this sample. */
  update(level: number, now: number): boolean {
    if (!this.speaking) {
      if (level >= this.onLevel) {
        this.speaking = true;
        this.quietSince = 0;
      }
      return this.speaking;
    }

    if (level >= this.offLevel) {
      this.quietSince = 0;
      return true;
    }
    if (this.quietSince === 0) this.quietSince = now;
    if (now - this.quietSince >= this.holdMs) this.speaking = false;
    return this.speaking;
  }
}

export const SAMPLE_INTERVAL_MS = 100;

export interface SpeakingOptions {
  onChange: (id: string, speaking: boolean) => void;
  /** Absent in a test, and in any browser too old to have one — in which case
   *  nothing is watched and nobody ever lights up, which is a missing
   *  indicator rather than a broken call. */
  createContext?: () => AudioContext | null;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

interface Watched {
  analyser: AnalyserNode;
  source: MediaStreamAudioSourceNode;
  buffer: Float32Array<ArrayBuffer>;
  gate: SpeechGate;
}

export function browserAudioContext(): AudioContext | null {
  return typeof AudioContext === 'undefined' ? null : new AudioContext();
}

export class SpeakingDetector {
  private readonly watched = new Map<string, Watched>();
  private context: AudioContext | null = null;
  private timer: unknown = null;

  private readonly onChange: (id: string, speaking: boolean) => void;
  private readonly createContext: () => AudioContext | null;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  constructor(options: SpeakingOptions) {
    this.onChange = options.onChange;
    this.createContext = options.createContext ?? browserAudioContext;
    this.now = options.now ?? (() => Date.now());
    this.setTimer = options.setTimer ?? ((fn, ms) => setInterval(fn, ms));
    this.clearTimer = options.clearTimer ?? ((h) => clearInterval(h as ReturnType<typeof setInterval>));
  }

  watch(id: string, track: MediaStreamTrack): void {
    if (track.kind !== 'audio' || this.watched.has(id)) return;
    const context = this.ensureContext();
    if (!context) return;

    const source = context.createMediaStreamSource(new MediaStream([track]));
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    this.watched.set(id, {
      analyser,
      source,
      buffer: new Float32Array(analyser.fftSize),
      gate: new SpeechGate()
    });

    if (this.timer === null) {
      this.timer = this.setTimer(() => this.sample(), SAMPLE_INTERVAL_MS);
    }
  }

  unwatch(id: string): void {
    const entry = this.watched.get(id);
    if (!entry) return;
    entry.source.disconnect();
    this.watched.delete(id);
    if (entry.gate.active) this.onChange(id, false);
    if (this.watched.size === 0) this.stopTimer();
  }

  stop(): void {
    for (const id of [...this.watched.keys()]) this.unwatch(id);
    this.stopTimer();
    void this.context?.close().catch(() => {});
    this.context = null;
  }

  private ensureContext(): AudioContext | null {
    if (!this.context) this.context = this.createContext();
    return this.context;
  }

  private stopTimer(): void {
    if (this.timer === null) return;
    this.clearTimer(this.timer);
    this.timer = null;
  }

  private sample(): void {
    const now = this.now();
    for (const [id, entry] of this.watched) {
      entry.analyser.getFloatTimeDomainData(entry.buffer);
      const was = entry.gate.active;
      const is = entry.gate.update(rms(entry.buffer), now);
      if (is !== was) this.onChange(id, is);
    }
  }
}
